/**
 * Connect HTML/No-CMS Site Page
 * Allows users who selected "No CMS" to connect their blog by URL.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Globe, Key, ChevronRight, ArrowLeft, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import vizzexLogo from '../../assets/VIZZEX.250.png';

type PageStatus = 'form' | 'loading' | 'success' | 'error';

const CMS_OPTIONS = [
  // Most likely to upgrade — show first
  { value: 'HubSpot',                label: 'HubSpot' },
  { value: 'WordPress',              label: 'WordPress' },
  // Popular hosted / SaaS
  { value: 'Shopify',                label: 'Shopify' },
  { value: 'Wix',                    label: 'Wix' },
  { value: 'Squarespace',            label: 'Squarespace' },
  { value: 'Webflow',                label: 'Webflow' },
  { value: 'Ghost',                  label: 'Ghost' },
  { value: 'Duda',                   label: 'Duda' },
  { value: 'Tilda',                  label: 'Tilda' },
  // Developer / headless / enterprise
  { value: 'Craft CMS',              label: 'Craft CMS' },
  { value: 'Contentful',             label: 'Contentful' },
  { value: 'Sanity',                 label: 'Sanity' },
  { value: 'Strapi',                 label: 'Strapi' },
  { value: 'Prismic',                label: 'Prismic' },
  { value: 'Sitecore',               label: 'Sitecore' },
  { value: 'Kentico',                label: 'Kentico' },
  { value: 'Adobe Experience Manager', label: 'Adobe Experience Manager' },
  { value: 'Umbraco',                label: 'Umbraco' },
  { value: 'Joomla',                 label: 'Joomla' },
  { value: 'Drupal',                 label: 'Drupal' },
  { value: 'Concrete CMS',           label: 'Concrete CMS' },
  { value: 'Typo3',                  label: 'TYPO3' },
  { value: 'other',                  label: 'Other (please specify)' },
];

export default function ConnectHtmlSitePage() {
  const [status, setStatus] = useState<PageStatus>('form');
  const [blogArchiveUrl, setBlogArchiveUrl] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [siteName, setSiteName] = useState('');
  const [cmsPlatform, setCmsPlatform] = useState('');
  const [cmsPlatformOther, setCmsPlatformOther] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const url = blogArchiveUrl.trim();
    if (!url.startsWith('http')) {
      setError('Blog archive URL must start with https://');
      return;
    }
    if (!licenseKey.trim()) {
      setError('License key is required');
      return;
    }
    if (!claudeApiKey.trim()) {
      setError('Claude API key is required');
      return;
    }
    if (cmsPlatform === 'other' && !cmsPlatformOther.trim()) {
      setError('Please enter your CMS name');
      return;
    }

    setStatus('loading');

    try {
      const result = await authApi.connectHtmlSite({
        blogArchiveUrl: url,
        licenseKey: licenseKey.trim(),
        claudeApiKey: claudeApiKey.trim(),
        siteName: siteName.trim() || undefined,
        clientCmsPlatform: cmsPlatform || undefined,
        clientCmsPlatformOther: cmsPlatform === 'other' ? cmsPlatformOther.trim() : undefined,
      });

      if (result.success) {
        await refreshUser();
        setStatus('success');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      } else {
        setError(result.error || 'Failed to connect blog');
        setStatus('form');
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error || e.message || 'Failed to connect blog');
      setStatus('form');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md animate-slide-up text-center">
          <img src={vizzexLogo} alt="VizzEx Pro" className="w-[250px] mx-auto mb-6" />
          <div className="glass rounded-2xl p-8">
            <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Blog Connected!</h2>
            <p className="text-[var(--text-secondary)]">Redirecting to your dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={vizzexLogo} alt="VizzEx Pro" className="w-[250px] mx-auto mb-4" />
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          <p className="text-[var(--text-secondary)] mt-2">Connect your blog by URL</p>
        </div>

        <div className="glass rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm flex gap-2 items-start">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {/* Blog archive URL */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Blog Archive URL <span className="text-[var(--color-error)]">*</span>
              </label>
              <p className="text-xs text-[var(--text-secondary)] mb-2">
                The URL of your blog's listing/archive page (e.g. https://example.com/blog)
              </p>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="url"
                  value={blogArchiveUrl}
                  onChange={e => setBlogArchiveUrl(e.target.value)}
                  placeholder="https://example.com/blog"
                  required
                  className="w-full bg-[var(--bg-tertiary)] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[var(--color-primary-400)] transition-colors"
                />
              </div>
            </div>

            {/* License key */}
            <div>
              <label className="block text-sm font-medium mb-2">
                License Key <span className="text-[var(--color-error)]">*</span>
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  value={licenseKey}
                  onChange={e => setLicenseKey(e.target.value)}
                  placeholder="PLM-XXXX-XXXX-XXXX"
                  required
                  autoComplete="off"
                  className="w-full bg-[var(--bg-tertiary)] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[var(--color-primary-400)] transition-colors"
                />
              </div>
            </div>

            {/* Claude API key */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Claude API Key <span className="text-[var(--color-error)]">*</span>
              </label>
              <p className="text-xs text-[var(--text-secondary)] mb-2">
                Required for content analysis and link recommendations
              </p>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="password"
                  value={claudeApiKey}
                  onChange={e => setClaudeApiKey(e.target.value)}
                  placeholder="sk-ant-..."
                  required
                  className="w-full bg-[var(--bg-tertiary)] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[var(--color-primary-400)] transition-colors"
                />
              </div>
            </div>

            {/* Site name (optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Site Name <span className="text-[var(--text-secondary)] font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  value={siteName}
                  onChange={e => setSiteName(e.target.value)}
                  placeholder="My Blog"
                  autoComplete="off"
                  className="w-full bg-[var(--bg-tertiary)] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[var(--color-primary-400)] transition-colors"
                />
              </div>
            </div>

            {/* CMS platform (optional — market research) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                What CMS do you use? <span className="text-[var(--text-secondary)] font-normal">(optional)</span>
              </label>
              <p className="text-xs text-[var(--text-secondary)] mb-2">
                Helps us prioritize native integrations for the platforms you use most.
              </p>
              <div className="relative">
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" />
                <select
                  value={cmsPlatform}
                  onChange={e => {
                    setCmsPlatform(e.target.value);
                    if (e.target.value !== 'other') setCmsPlatformOther('');
                  }}
                  className="w-full bg-[var(--bg-tertiary)] border border-white/10 rounded-lg pl-4 pr-10 py-3 text-white appearance-none focus:outline-none focus:border-[var(--color-primary-400)] transition-colors"
                >
                  <option value="">— Select your CMS —</option>
                  {CMS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Free-text entry when "Other" is selected */}
              {cmsPlatform === 'other' && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={cmsPlatformOther}
                    onChange={e => setCmsPlatformOther(e.target.value)}
                    placeholder="Enter your CMS name"
                    maxLength={80}
                    autoComplete="off"
                    className="w-full bg-[var(--bg-tertiary)] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[var(--color-primary-400)] transition-colors"
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Connecting…
                </>
              ) : (
                <>
                  Connect Blog
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm text-[var(--text-secondary)]">
          <Link to="/dashboard" className="text-[var(--color-primary-400)] hover:text-[var(--color-primary-300)] inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
        </p>
      </div>
    </div>
  );
}
